import type { LocaleCode } from "@repo/shared/types";

// Local translations — all 17 supported locales (QL-637 acceptance criterion:
// translated in every supported language). `accountNotLinked`, `signupDisabled`,
// `lastPasswordAdmin`, `identityChanged`, `linkEmailTitle`, `linkEmailSent`,
// `emailRequired` and `providerMisconfigured` keep the en_GB/nb_NO wording from
// before QL-637 verbatim.
export const T: Record<
  LocaleCode,
  {
    title: string;
    linkEmailTitle: string;
    linkEmailSent: string;
    cancelled: string;
    expired: string;
    idpRejectedRequest: string;
    idpUnavailable: string;
    idpTimeout: string;
    tokenNotVerified: string;
    missingUserInfo: string;
    emailRequired: string;
    providerMisconfigured: string;
    accountNotLinked: string;
    signupDisabled: string;
    accountUnavailable: string;
    lastPasswordAdmin: string;
    identityChanged: string;
    unknown: string;
    adminHintLabel: string;
    hintTokenNotVerified: string;
    hintMissingUserInfo: string;
    hintProviderMisconfigured: string;
    hintIdpRejectedRequest: string;
    hintAccountNotLinked: string;
    referenceLabel: string;
    providerMessageLabel: string;
    referenceHelp: string;
    copy: string;
    copied: string;
  }
> = {
  en_GB: {
    title: "SSO sign-in failed",
    linkEmailTitle: "Check your email",
    linkEmailSent:
      "To connect single sign-on to your existing account, follow the instructions in the email we just sent you.",
    cancelled: "Sign-in was cancelled or denied by your identity provider.",
    expired: "The sign-in attempt expired or was started in another browser. Please try again.",
    idpRejectedRequest: "Your identity provider rejected the sign-in request.",
    idpUnavailable:
      "Your identity provider reported a temporary problem. Try again in a few minutes.",
    idpTimeout: "Your identity provider did not answer in time. Please try again.",
    tokenNotVerified: "The sign-in response from your identity provider could not be verified.",
    missingUserInfo:
      "Your identity provider did not send an email address or user ID for your account.",
    emailRequired:
      "Your identity provider did not send an email address. Contact your administrator.",
    providerMisconfigured:
      "SSO is not set up correctly for this organization. Contact your administrator.",
    accountNotLinked:
      "Your SSO account could not be linked to a user in this organization. Contact your administrator.",
    signupDisabled: "No user exists for your SSO account in this organization.",
    accountUnavailable:
      "Your account is no longer available in this organization. Contact your administrator.",
    lastPasswordAdmin:
      "Your account is the last administrator who can sign in without single sign-on, so it can't be connected to SSO. Sign in with your password.",
    identityChanged:
      "Your single sign-on identity does not match the one connected to your account. Contact your administrator.",
    unknown: "Something went wrong during sign-in. Please try again.",
    adminHintLabel: "For administrators:",
    hintTokenNotVerified:
      "The ID token's issuer, audience or signature does not match the saved SSO settings. Check the Issuer URL and Client ID in SSO settings, then try signing in again.",
    hintMissingUserInfo:
      "The identity provider must include an email claim and a subject (sub) in the ID token or userinfo. Add the email claim in the identity provider (for AD FS: an outgoing claim rule), then try signing in again.",
    hintProviderMisconfigured:
      "Check that the client ID and client secret in SSO settings are current (an expired secret is a common cause), then save the issuer URL again to refresh the provider's endpoints and run the test sign-in.",
    hintIdpRejectedRequest:
      "Check that the redirect URI shown in SSO settings is registered at the identity provider and that the client ID and secret are current.",
    hintAccountNotLinked:
      "Check that the user exists in this organization with the email address the identity provider sends.",
    referenceLabel: "Reference:",
    providerMessageLabel: "Message from your sign-in provider:",
    referenceHelp:
      "Give this reference to your administrator; it identifies this sign-in attempt in the logs.",
    copy: "Copy",
    copied: "Copied",
  },
  fr_FR: {
    title: "Échec de la connexion SSO",
    linkEmailTitle: "Vérifiez vos e-mails",
    linkEmailSent:
      "Pour connecter l'authentification unique à votre compte existant, suivez les instructions de l'e-mail que nous venons de vous envoyer.",
    cancelled: "La connexion a été annulée ou refusée par votre fournisseur d'identité.",
    expired:
      "La tentative de connexion a expiré ou a été lancée dans un autre navigateur. Veuillez réessayer.",
    idpRejectedRequest: "Votre fournisseur d'identité a rejeté la demande de connexion.",
    idpUnavailable:
      "Votre fournisseur d'identité a signalé un problème temporaire. Réessayez dans quelques minutes.",
    idpTimeout: "Votre fournisseur d'identité n'a pas répondu à temps. Veuillez réessayer.",
    tokenNotVerified:
      "La réponse de connexion de votre fournisseur d'identité n'a pas pu être vérifiée.",
    missingUserInfo:
      "Votre fournisseur d'identité n'a pas envoyé d'adresse e-mail ni d'identifiant utilisateur pour votre compte.",
    emailRequired:
      "Votre fournisseur d'identité n'a pas envoyé d'adresse e-mail. Contactez votre administrateur.",
    providerMisconfigured:
      "Le SSO n'est pas correctement configuré pour cette organisation. Contactez votre administrateur.",
    accountNotLinked:
      "Votre compte SSO n'a pas pu être lié à un utilisateur de cette organisation. Contactez votre administrateur.",
    signupDisabled: "Aucun utilisateur n'existe pour votre compte SSO dans cette organisation.",
    accountUnavailable:
      "Votre compte n'est plus disponible dans cette organisation. Contactez votre administrateur.",
    lastPasswordAdmin:
      "Votre compte est le dernier administrateur pouvant se connecter sans authentification unique, il ne peut donc pas être connecté au SSO. Connectez-vous avec votre mot de passe.",
    identityChanged:
      "Votre identité d'authentification unique ne correspond pas à celle liée à votre compte. Contactez votre administrateur.",
    unknown: "Une erreur s'est produite lors de la connexion. Veuillez réessayer.",
    adminHintLabel: "Pour les administrateurs :",
    hintTokenNotVerified:
      "L'émetteur, l'audience ou la signature du jeton d'identité ne correspond pas aux paramètres SSO enregistrés. Vérifiez l'URL de l'émetteur et l'ID client dans les paramètres SSO, puis réessayez de vous connecter.",
    hintMissingUserInfo:
      "Le fournisseur d'identité doit inclure une revendication d'e-mail et un sujet (sub) dans le jeton d'identité ou les informations utilisateur. Ajoutez la revendication d'e-mail chez le fournisseur d'identité (pour AD FS : une règle de revendication sortante), puis réessayez de vous connecter.",
    hintProviderMisconfigured:
      "Vérifiez que l'ID client et le secret dans les paramètres SSO sont à jour (un secret expiré est une cause fréquente), puis enregistrez à nouveau l'URL de l'émetteur pour actualiser les points de terminaison du fournisseur et lancez la connexion de test.",
    hintIdpRejectedRequest:
      "Vérifiez que l'URI de redirection affichée dans les paramètres SSO est enregistrée chez le fournisseur d'identité et que l'ID client et le secret sont à jour.",
    hintAccountNotLinked:
      "Vérifiez que l'utilisateur existe dans cette organisation avec l'adresse e-mail envoyée par le fournisseur d'identité.",
    referenceLabel: "Référence :",
    providerMessageLabel: "Message de votre fournisseur de connexion :",
    referenceHelp:
      "Communiquez cette référence à votre administrateur ; elle identifie cette tentative de connexion dans les journaux.",
    copy: "Copier",
    copied: "Copié",
  },
  es_ES: {
    title: "Error al iniciar sesión con SSO",
    linkEmailTitle: "Revisa tu correo electrónico",
    linkEmailSent:
      "Para conectar el inicio de sesión único a tu cuenta existente, sigue las instrucciones del correo electrónico que acabamos de enviarte.",
    cancelled: "El inicio de sesión fue cancelado o denegado por tu proveedor de identidad.",
    expired:
      "El intento de inicio de sesión caducó o se inició en otro navegador. Inténtalo de nuevo.",
    idpRejectedRequest: "Tu proveedor de identidad rechazó la solicitud de inicio de sesión.",
    idpUnavailable:
      "Tu proveedor de identidad informó un problema temporal. Inténtalo de nuevo en unos minutos.",
    idpTimeout: "Tu proveedor de identidad no respondió a tiempo. Inténtalo de nuevo.",
    tokenNotVerified:
      "No se pudo verificar la respuesta de inicio de sesión de tu proveedor de identidad.",
    missingUserInfo:
      "Tu proveedor de identidad no envió una dirección de correo electrónico ni un ID de usuario para tu cuenta.",
    emailRequired:
      "Tu proveedor de identidad no envió una dirección de correo electrónico. Ponte en contacto con tu administrador.",
    providerMisconfigured:
      "El SSO no está configurado correctamente para esta organización. Ponte en contacto con tu administrador.",
    accountNotLinked:
      "Tu cuenta SSO no se pudo vincular a un usuario de esta organización. Ponte en contacto con tu administrador.",
    signupDisabled: "No existe ningún usuario para tu cuenta SSO en esta organización.",
    accountUnavailable:
      "Tu cuenta ya no está disponible en esta organización. Ponte en contacto con tu administrador.",
    lastPasswordAdmin:
      "Tu cuenta es el último administrador que puede iniciar sesión sin inicio de sesión único, por lo que no se puede conectar al SSO. Inicia sesión con tu contraseña.",
    identityChanged:
      "Tu identidad de inicio de sesión único no coincide con la conectada a tu cuenta. Ponte en contacto con tu administrador.",
    unknown: "Se produjo un error durante el inicio de sesión. Inténtalo de nuevo.",
    adminHintLabel: "Para administradores:",
    hintTokenNotVerified:
      "El emisor, la audiencia o la firma del token de identidad no coincide con la configuración de SSO guardada. Comprueba la URL del emisor y el ID de cliente en la configuración de SSO y luego intenta iniciar sesión de nuevo.",
    hintMissingUserInfo:
      "El proveedor de identidad debe incluir una reclamación de correo electrónico y un sujeto (sub) en el token de identidad o en la información del usuario. Añade la reclamación de correo electrónico en el proveedor de identidad (para AD FS: una regla de reclamación saliente) y luego intenta iniciar sesión de nuevo.",
    hintProviderMisconfigured:
      "Comprueba que el ID de cliente y el secreto de la configuración de SSO estén vigentes (un secreto caducado es una causa habitual) y, después, guarda de nuevo la URL del emisor para actualizar los puntos de conexión del proveedor y ejecuta el inicio de sesión de prueba.",
    hintIdpRejectedRequest:
      "Comprueba que el URI de redirección mostrado en la configuración de SSO esté registrado en el proveedor de identidad y que el ID de cliente y el secreto estén vigentes.",
    hintAccountNotLinked:
      "Comprueba que el usuario exista en esta organización con la dirección de correo electrónico que envía el proveedor de identidad.",
    referenceLabel: "Referencia:",
    providerMessageLabel: "Mensaje de tu proveedor de inicio de sesión:",
    referenceHelp:
      "Proporciona esta referencia a tu administrador; identifica este intento de inicio de sesión en los registros.",
    copy: "Copiar",
    copied: "Copiado",
  },
  pt_BR: {
    title: "Falha no login com SSO",
    linkEmailTitle: "Verifique seu e-mail",
    linkEmailSent:
      "Para conectar o login único à sua conta existente, siga as instruções do e-mail que acabamos de enviar.",
    cancelled: "O login foi cancelado ou negado pelo seu provedor de identidade.",
    expired: "A tentativa de login expirou ou foi iniciada em outro navegador. Tente novamente.",
    idpRejectedRequest: "Seu provedor de identidade rejeitou a solicitação de login.",
    idpUnavailable:
      "Seu provedor de identidade relatou um problema temporário. Tente novamente em alguns minutos.",
    idpTimeout: "Seu provedor de identidade não respondeu a tempo. Tente novamente.",
    tokenNotVerified:
      "Não foi possível verificar a resposta de login do seu provedor de identidade.",
    missingUserInfo:
      "Seu provedor de identidade não enviou um endereço de e-mail ou ID de usuário para sua conta.",
    emailRequired:
      "Seu provedor de identidade não enviou um endereço de e-mail. Entre em contato com o administrador.",
    providerMisconfigured:
      "O SSO não está configurado corretamente para esta organização. Entre em contato com o administrador.",
    accountNotLinked:
      "Sua conta SSO não pôde ser vinculada a um usuário desta organização. Entre em contato com o administrador.",
    signupDisabled: "Não existe nenhum usuário para sua conta SSO nesta organização.",
    accountUnavailable:
      "Sua conta não está mais disponível nesta organização. Entre em contato com o administrador.",
    lastPasswordAdmin:
      "Sua conta é o último administrador que pode fazer login sem login único, portanto não pode ser conectada ao SSO. Faça login com sua senha.",
    identityChanged:
      "Sua identidade de login único não corresponde à conectada à sua conta. Entre em contato com o administrador.",
    unknown: "Algo deu errado durante o login. Tente novamente.",
    adminHintLabel: "Para administradores:",
    hintTokenNotVerified:
      "O emissor, o público-alvo ou a assinatura do token de identidade não correspondem às configurações de SSO salvas. Verifique a URL do emissor e o ID do cliente nas configurações de SSO e tente fazer login novamente.",
    hintMissingUserInfo:
      "O provedor de identidade deve incluir uma declaração de e-mail e um assunto (sub) no token de identidade ou nas informações do usuário. Adicione a declaração de e-mail no provedor de identidade (para AD FS: uma regra de declaração de saída) e tente fazer login novamente.",
    hintProviderMisconfigured:
      "Verifique se o ID do cliente e o segredo nas configurações de SSO estão atualizados (um segredo expirado é uma causa comum) e, em seguida, salve a URL do emissor novamente para atualizar os endpoints do provedor e execute o login de teste.",
    hintIdpRejectedRequest:
      "Verifique se o URI de redirecionamento mostrado nas configurações de SSO está registrado no provedor de identidade e se o ID do cliente e o segredo estão atualizados.",
    hintAccountNotLinked:
      "Verifique se o usuário existe nesta organização com o endereço de e-mail enviado pelo provedor de identidade.",
    referenceLabel: "Referência:",
    providerMessageLabel: "Mensagem do seu provedor de login:",
    referenceHelp:
      "Informe esta referência ao seu administrador; ela identifica esta tentativa de login nos registros.",
    copy: "Copiar",
    copied: "Copiado",
  },
  de_DE: {
    title: "SSO-Anmeldung fehlgeschlagen",
    linkEmailTitle: "Prüfen Sie Ihre E-Mails",
    linkEmailSent:
      "Um Single Sign-on mit Ihrem bestehenden Konto zu verbinden, folgen Sie den Anweisungen in der E-Mail, die wir Ihnen gerade gesendet haben.",
    cancelled: "Die Anmeldung wurde von Ihrem Identitätsanbieter abgebrochen oder abgelehnt.",
    expired:
      "Der Anmeldeversuch ist abgelaufen oder wurde in einem anderen Browser gestartet. Bitte versuchen Sie es erneut.",
    idpRejectedRequest: "Ihr Identitätsanbieter hat die Anmeldeanfrage abgelehnt.",
    idpUnavailable:
      "Ihr Identitätsanbieter meldete ein vorübergehendes Problem. Versuchen Sie es in einigen Minuten erneut.",
    idpTimeout:
      "Ihr Identitätsanbieter hat nicht rechtzeitig geantwortet. Bitte versuchen Sie es erneut.",
    tokenNotVerified: "Die Anmeldeantwort Ihres Identitätsanbieters konnte nicht überprüft werden.",
    missingUserInfo:
      "Ihr Identitätsanbieter hat keine E-Mail-Adresse oder Benutzer-ID für Ihr Konto gesendet.",
    emailRequired:
      "Ihr Identitätsanbieter hat keine E-Mail-Adresse gesendet. Wenden Sie sich an Ihren Administrator.",
    providerMisconfigured:
      "Single Sign-on ist für diese Organisation nicht korrekt eingerichtet. Wenden Sie sich an Ihren Administrator.",
    accountNotLinked:
      "Ihr SSO-Konto konnte keinem Benutzer in dieser Organisation zugeordnet werden. Wenden Sie sich an Ihren Administrator.",
    signupDisabled: "Für Ihr SSO-Konto existiert kein Benutzer in dieser Organisation.",
    accountUnavailable:
      "Ihr Konto ist in dieser Organisation nicht mehr verfügbar. Wenden Sie sich an Ihren Administrator.",
    lastPasswordAdmin:
      "Ihr Konto ist der letzte Administrator, der sich ohne Single Sign-on anmelden kann, daher kann es nicht mit SSO verbunden werden. Melden Sie sich mit Ihrem Passwort an.",
    identityChanged:
      "Ihre Single-Sign-on-Identität stimmt nicht mit der überein, die mit Ihrem Konto verbunden ist. Wenden Sie sich an Ihren Administrator.",
    unknown: "Bei der Anmeldung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    adminHintLabel: "Für Administratoren:",
    hintTokenNotVerified:
      "Aussteller, Zielgruppe oder Signatur des ID-Tokens stimmen nicht mit den gespeicherten SSO-Einstellungen überein. Prüfen Sie die Aussteller-URL und die Client-ID in den SSO-Einstellungen und versuchen Sie dann, sich erneut anzumelden.",
    hintMissingUserInfo:
      "Der Identitätsanbieter muss einen E-Mail-Claim und ein Subjekt (sub) im ID-Token oder in den Benutzerinformationen enthalten. Fügen Sie den E-Mail-Claim beim Identitätsanbieter hinzu (bei AD FS: eine ausgehende Claim-Regel) und versuchen Sie dann, sich erneut anzumelden.",
    hintProviderMisconfigured:
      "Prüfen Sie, ob Client-ID und -Secret in den SSO-Einstellungen aktuell sind (ein abgelaufenes Secret ist eine häufige Ursache). Speichern Sie dann die Aussteller-URL erneut, um die Endpunkte des Anbieters zu aktualisieren, und führen Sie die Testanmeldung aus.",
    hintIdpRejectedRequest:
      "Prüfen Sie, ob die in den SSO-Einstellungen angezeigte Redirect-URI beim Identitätsanbieter registriert ist und ob Client-ID und -Secret aktuell sind.",
    hintAccountNotLinked:
      "Prüfen Sie, ob der Benutzer in dieser Organisation mit der vom Identitätsanbieter gesendeten E-Mail-Adresse existiert.",
    referenceLabel: "Referenz:",
    providerMessageLabel: "Meldung Ihres Anmeldeanbieters:",
    referenceHelp:
      "Geben Sie diese Referenz an Ihren Administrator weiter; sie identifiziert diesen Anmeldeversuch in den Protokollen.",
    copy: "Kopieren",
    copied: "Kopiert",
  },
  it_IT: {
    title: "Accesso SSO non riuscito",
    linkEmailTitle: "Controlla la tua email",
    linkEmailSent:
      "Per collegare il single sign-on al tuo account esistente, segui le istruzioni nell'email che ti abbiamo appena inviato.",
    cancelled: "L'accesso è stato annullato o rifiutato dal tuo provider di identità.",
    expired: "Il tentativo di accesso è scaduto o è stato avviato in un altro browser. Riprova.",
    idpRejectedRequest: "Il tuo provider di identità ha rifiutato la richiesta di accesso.",
    idpUnavailable:
      "Il tuo provider di identità ha segnalato un problema temporaneo. Riprova tra qualche minuto.",
    idpTimeout: "Il tuo provider di identità non ha risposto in tempo. Riprova.",
    tokenNotVerified:
      "Non è stato possibile verificare la risposta di accesso del tuo provider di identità.",
    missingUserInfo:
      "Il tuo provider di identità non ha inviato un indirizzo email o un ID utente per il tuo account.",
    emailRequired:
      "Il tuo provider di identità non ha inviato un indirizzo email. Contatta il tuo amministratore.",
    providerMisconfigured:
      "L'SSO non è configurato correttamente per questa organizzazione. Contatta il tuo amministratore.",
    accountNotLinked:
      "Non è stato possibile collegare il tuo account SSO a un utente di questa organizzazione. Contatta il tuo amministratore.",
    signupDisabled: "Non esiste alcun utente per il tuo account SSO in questa organizzazione.",
    accountUnavailable:
      "Il tuo account non è più disponibile in questa organizzazione. Contatta il tuo amministratore.",
    lastPasswordAdmin:
      "Il tuo account è l'ultimo amministratore che può accedere senza single sign-on, quindi non può essere collegato all'SSO. Accedi con la tua password.",
    identityChanged:
      "La tua identità di single sign-on non corrisponde a quella collegata al tuo account. Contatta il tuo amministratore.",
    unknown: "Si è verificato un errore durante l'accesso. Riprova.",
    adminHintLabel: "Per gli amministratori:",
    hintTokenNotVerified:
      "L'emittente, il destinatario o la firma del token ID non corrispondono alle impostazioni SSO salvate. Controlla l'URL dell'emittente e l'ID client nelle impostazioni SSO, quindi prova ad accedere di nuovo.",
    hintMissingUserInfo:
      "Il provider di identità deve includere un claim email e un soggetto (sub) nel token ID o nelle informazioni utente. Aggiungi il claim email presso il provider di identità (per AD FS: una regola di claim in uscita), quindi prova ad accedere di nuovo.",
    hintProviderMisconfigured:
      "Verifica che l'ID client e il segreto nelle impostazioni SSO siano aggiornati (un segreto scaduto è una causa frequente), poi salva nuovamente l'URL dell'emittente per aggiornare gli endpoint del provider ed esegui l'accesso di prova.",
    hintIdpRejectedRequest:
      "Verifica che l'URI di reindirizzamento mostrato nelle impostazioni SSO sia registrato presso il provider di identità e che l'ID client e il segreto siano aggiornati.",
    hintAccountNotLinked:
      "Verifica che l'utente esista in questa organizzazione con l'indirizzo email inviato dal provider di identità.",
    referenceLabel: "Riferimento:",
    providerMessageLabel: "Messaggio dal tuo provider di accesso:",
    referenceHelp:
      "Fornisci questo riferimento al tuo amministratore; identifica questo tentativo di accesso nei log.",
    copy: "Copia",
    copied: "Copiato",
  },
  ar: {
    title: "فشل تسجيل الدخول عبر SSO",
    linkEmailTitle: "تحقق من بريدك الإلكتروني",
    linkEmailSent:
      "لربط تسجيل الدخول الموحد بحسابك الحالي، اتبع التعليمات الواردة في البريد الإلكتروني الذي أرسلناه لك للتو.",
    cancelled: "تم إلغاء تسجيل الدخول أو رفضه من قِبل موفر الهوية الخاص بك.",
    expired: "انتهت صلاحية محاولة تسجيل الدخول أو بدأت في متصفح آخر. يرجى المحاولة مرة أخرى.",
    idpRejectedRequest: "رفض موفر الهوية الخاص بك طلب تسجيل الدخول.",
    idpUnavailable: "أبلغ موفر الهوية الخاص بك عن مشكلة مؤقتة. حاول مرة أخرى خلال بضع دقائق.",
    idpTimeout: "لم يستجب موفر الهوية الخاص بك في الوقت المناسب. يرجى المحاولة مرة أخرى.",
    tokenNotVerified: "تعذر التحقق من استجابة تسجيل الدخول من موفر الهوية الخاص بك.",
    missingUserInfo: "لم يرسل موفر الهوية الخاص بك عنوان بريد إلكتروني أو معرّف مستخدم لحسابك.",
    emailRequired: "لم يرسل موفر الهوية الخاص بك عنوان بريد إلكتروني. تواصل مع المسؤول لديك.",
    providerMisconfigured:
      "تسجيل الدخول الموحد غير مُعد بشكل صحيح لهذه المؤسسة. تواصل مع المسؤول لديك.",
    accountNotLinked: "تعذر ربط حساب SSO الخاص بك بمستخدم في هذه المؤسسة. تواصل مع المسؤول لديك.",
    signupDisabled: "لا يوجد مستخدم لحساب SSO الخاص بك في هذه المؤسسة.",
    accountUnavailable: "لم يعد حسابك متاحًا في هذه المؤسسة. تواصل مع المسؤول لديك.",
    lastPasswordAdmin:
      "حسابك هو آخر حساب مسؤول يمكنه تسجيل الدخول دون تسجيل الدخول الموحد، لذا لا يمكن ربطه بـ SSO. سجّل الدخول بكلمة المرور الخاصة بك.",
    identityChanged:
      "هوية تسجيل الدخول الموحد الخاصة بك لا تطابق الهوية المرتبطة بحسابك. تواصل مع المسؤول لديك.",
    unknown: "حدث خطأ أثناء تسجيل الدخول. يرجى المحاولة مرة أخرى.",
    adminHintLabel: "للمسؤولين:",
    hintTokenNotVerified:
      "لا تطابق جهة إصدار رمز الهوية أو الجمهور أو التوقيع إعدادات SSO المحفوظة. تحقق من عنوان URL للمُصدر ومعرّف العميل في إعدادات SSO، ثم حاول تسجيل الدخول مرة أخرى.",
    hintMissingUserInfo:
      "يجب أن يتضمن موفر الهوية ادعاء البريد الإلكتروني وموضوعًا (sub) في رمز الهوية أو معلومات المستخدم. أضف ادعاء البريد الإلكتروني لدى موفر الهوية (لـ AD FS: قاعدة ادعاء صادرة)، ثم حاول تسجيل الدخول مرة أخرى.",
    hintProviderMisconfigured:
      "تحقق من أن معرّف العميل والسر في إعدادات SSO محدّثان (انتهاء صلاحية السر سبب شائع)، ثم احفظ عنوان URL للمُصدر مرة أخرى لتحديث نقاط نهاية الموفر وشغّل تسجيل الدخول التجريبي.",
    hintIdpRejectedRequest:
      "تحقق من أن عنوان URI لإعادة التوجيه الموضح في إعدادات SSO مسجَّل لدى موفر الهوية وأن معرّف العميل والسر محدّثان.",
    hintAccountNotLinked:
      "تحقق من وجود المستخدم في هذه المؤسسة بعنوان البريد الإلكتروني الذي يرسله موفر الهوية.",
    referenceLabel: "المرجع:",
    providerMessageLabel: "رسالة من موفر تسجيل الدخول:",
    referenceHelp: "قدّم هذا المرجع إلى المسؤول لديك؛ فهو يحدد محاولة تسجيل الدخول هذه في السجلات.",
    copy: "نسخ",
    copied: "تم النسخ",
  },
  ja_JP: {
    title: "SSOサインインに失敗しました",
    linkEmailTitle: "メールをご確認ください",
    linkEmailSent:
      "シングルサインオンを既存のアカウントに接続するには、お送りしたメールの指示に従ってください。",
    cancelled: "サインインはIDプロバイダーによってキャンセルまたは拒否されました。",
    expired:
      "サインインの試行が期限切れになったか、別のブラウザで開始されました。もう一度お試しください。",
    idpRejectedRequest: "IDプロバイダーがサインインリクエストを拒否しました。",
    idpUnavailable: "IDプロバイダーで一時的な問題が発生しました。数分後にもう一度お試しください。",
    idpTimeout: "IDプロバイダーが時間内に応答しませんでした。もう一度お試しください。",
    tokenNotVerified: "IDプロバイダーからのサインイン応答を検証できませんでした。",
    missingUserInfo:
      "IDプロバイダーがアカウントのメールアドレスまたはユーザーIDを送信しませんでした。",
    emailRequired: "IDプロバイダーがメールアドレスを送信しませんでした。管理者に連絡してください。",
    providerMisconfigured: "この組織ではSSOが正しく設定されていません。管理者に連絡してください。",
    accountNotLinked:
      "SSOアカウントをこの組織のユーザーにリンクできませんでした。管理者に連絡してください。",
    signupDisabled: "この組織にはSSOアカウント用のユーザーが存在しません。",
    accountUnavailable:
      "このアカウントはこの組織では使用できなくなりました。管理者に連絡してください。",
    lastPasswordAdmin:
      "このアカウントはシングルサインオンなしでサインインできる最後の管理者であるため、SSOに接続できません。パスワードでサインインしてください。",
    identityChanged:
      "シングルサインオンのIDがアカウントに接続されているものと一致しません。管理者に連絡してください。",
    unknown: "サインイン中に問題が発生しました。もう一度お試しください。",
    adminHintLabel: "管理者向け:",
    hintTokenNotVerified:
      "IDトークンの発行者、対象者、または署名が保存されたSSO設定と一致しません。SSO設定で発行者URLとクライアントIDを確認し、もう一度サインインしてみてください。",
    hintMissingUserInfo:
      "IDプロバイダーはIDトークンまたはユーザー情報にメールクレームとサブジェクト（sub）を含める必要があります。IDプロバイダーでメールクレームを追加し（AD FSの場合は送信クレームルール）、もう一度サインインしてみてください。",
    hintProviderMisconfigured:
      "SSO設定のクライアントIDとシークレットが最新であることを確認し（期限切れのシークレットがよくある原因です）、発行者URLを再保存してプロバイダーのエンドポイントを更新してから、テストサインインを実行してください。",
    hintIdpRejectedRequest:
      "SSO設定に表示されているリダイレクトURIがIDプロバイダーに登録されていること、およびクライアントIDとシークレットが最新であることを確認してください。",
    hintAccountNotLinked:
      "IDプロバイダーが送信するメールアドレスでこの組織にユーザーが存在することを確認してください。",
    referenceLabel: "参照番号:",
    providerMessageLabel: "サインインプロバイダーからのメッセージ:",
    referenceHelp:
      "この参照番号を管理者に伝えてください。ログ内でこのサインイン試行を識別できます。",
    copy: "コピー",
    copied: "コピーしました",
  },
  hi_IN: {
    title: "SSO साइन-इन विफल",
    linkEmailTitle: "अपना ईमेल जांचें",
    linkEmailSent:
      "सिंगल साइन-ऑन को अपने मौजूदा खाते से जोड़ने के लिए, हमने अभी आपको भेजे गए ईमेल में दिए गए निर्देशों का पालन करें।",
    cancelled: "साइन-इन आपके आइडेंटिटी प्रोवाइडर द्वारा रद्द या अस्वीकृत कर दिया गया।",
    expired: "साइन-इन प्रयास समाप्त हो गया या किसी अन्य ब्राउज़र में शुरू किया गया था। कृपया फिर से प्रयास करें।",
    idpRejectedRequest: "आपके आइडेंटिटी प्रोवाइडर ने साइन-इन अनुरोध अस्वीकार कर दिया।",
    idpUnavailable: "आपके आइडेंटिटी प्रोवाइडर ने एक अस्थायी समस्या की सूचना दी। कुछ मिनटों में फिर से प्रयास करें।",
    idpTimeout: "आपके आइडेंटिटी प्रोवाइडर ने समय पर जवाब नहीं दिया। कृपया फिर से प्रयास करें।",
    tokenNotVerified: "आपके आइडेंटिटी प्रोवाइडर की साइन-इन प्रतिक्रिया सत्यापित नहीं की जा सकी।",
    missingUserInfo: "आपके आइडेंटिटी प्रोवाइडर ने आपके खाते के लिए ईमेल पता या उपयोगकर्ता आईडी नहीं भेजी।",
    emailRequired: "आपके आइडेंटिटी प्रोवाइडर ने ईमेल पता नहीं भेजा। अपने एडमिनिस्ट्रेटर से संपर्क करें।",
    providerMisconfigured: "इस संगठन के लिए SSO सही तरीके से सेट अप नहीं है। अपने एडमिनिस्ट्रेटर से संपर्क करें।",
    accountNotLinked:
      "आपका SSO खाता इस संगठन में किसी उपयोगकर्ता से लिंक नहीं किया जा सका। अपने एडमिनिस्ट्रेटर से संपर्क करें।",
    signupDisabled: "इस संगठन में आपके SSO खाते के लिए कोई उपयोगकर्ता मौजूद नहीं है।",
    accountUnavailable: "आपका खाता अब इस संगठन में उपलब्ध नहीं है। अपने एडमिनिस्ट्रेटर से संपर्क करें।",
    lastPasswordAdmin:
      "आपका खाता अंतिम एडमिनिस्ट्रेटर है जो सिंगल साइन-ऑन के बिना साइन इन कर सकता है, इसलिए इसे SSO से नहीं जोड़ा जा सकता। अपने पासवर्ड से साइन इन करें।",
    identityChanged:
      "आपकी सिंगल साइन-ऑन पहचान आपके खाते से जुड़ी पहचान से मेल नहीं खाती। अपने एडमिनिस्ट्रेटर से संपर्क करें।",
    unknown: "साइन-इन के दौरान कुछ गलत हो गया। कृपया फिर से प्रयास करें।",
    adminHintLabel: "एडमिनिस्ट्रेटर के लिए:",
    hintTokenNotVerified:
      "आईडी टोकन का जारीकर्ता, ऑडियंस या हस्ताक्षर सहेजी गई SSO सेटिंग्स से मेल नहीं खाता। SSO सेटिंग्स में इश्यूअर URL और क्लाइंट आईडी जांचें, फिर से साइन इन करने का प्रयास करें।",
    hintMissingUserInfo:
      "आइडेंटिटी प्रोवाइडर को आईडी टोकन या यूज़रइन्फो में एक ईमेल क्लेम और एक सब्जेक्ट (sub) शामिल करना चाहिए। आइडेंटिटी प्रोवाइडर में ईमेल क्लेम जोड़ें (AD FS के लिए: एक आउटगोइंग क्लेम रूल), फिर से साइन इन करने का प्रयास करें।",
    hintProviderMisconfigured:
      "जांचें कि SSO सेटिंग्स में क्लाइंट आईडी व सीक्रेट वर्तमान हैं (एक्सपायर हुआ सीक्रेट एक आम कारण है), फिर प्रोवाइडर के एंडपॉइंट्स को रिफ्रेश करने के लिए इश्यूअर URL फिर से सहेजें और टेस्ट साइन-इन चलाएं।",
    hintIdpRejectedRequest:
      "जांचें कि SSO सेटिंग्स में दिखाया गया रीडायरेक्ट URI आइडेंटिटी प्रोवाइडर में रजिस्टर है और क्लाइंट आईडी व सीक्रेट वर्तमान हैं।",
    hintAccountNotLinked:
      "जांचें कि उपयोगकर्ता इस संगठन में उस ईमेल पते के साथ मौजूद है जो आइडेंटिटी प्रोवाइडर भेजता है।",
    referenceLabel: "संदर्भ:",
    providerMessageLabel: "आपके साइन-इन प्रोवाइडर का संदेश:",
    referenceHelp: "यह संदर्भ अपने एडमिनिस्ट्रेटर को दें; यह लॉग में इस साइन-इन प्रयास की पहचान करता है।",
    copy: "कॉपी करें",
    copied: "कॉपी हो गया",
  },
  zh_CN: {
    title: "SSO 登录失败",
    linkEmailTitle: "请查看您的邮箱",
    linkEmailSent: "要将单点登录与您现有的账户关联，请按照我们刚刚发送给您的邮件中的说明操作。",
    cancelled: "登录已被您的身份提供商取消或拒绝。",
    expired: "登录尝试已过期，或在其他浏览器中启动。请重试。",
    idpRejectedRequest: "您的身份提供商拒绝了登录请求。",
    idpUnavailable: "您的身份提供商报告了一个临时问题。请在几分钟后重试。",
    idpTimeout: "您的身份提供商未及时响应。请重试。",
    tokenNotVerified: "无法验证来自您的身份提供商的登录响应。",
    missingUserInfo: "您的身份提供商未发送您账户的电子邮件地址或用户 ID。",
    emailRequired: "您的身份提供商未发送电子邮件地址。请联系您的管理员。",
    providerMisconfigured: "此组织的 SSO 未正确设置。请联系您的管理员。",
    accountNotLinked: "您的 SSO 账户无法关联到此组织中的用户。请联系您的管理员。",
    signupDisabled: "此组织中不存在与您的 SSO 账户对应的用户。",
    accountUnavailable: "您的账户在此组织中已不可用。请联系您的管理员。",
    lastPasswordAdmin:
      "您的账户是可以不通过单点登录即可登录的最后一位管理员，因此无法连接到 SSO。请使用密码登录。",
    identityChanged: "您的单点登录身份与关联到您账户的身份不匹配。请联系您的管理员。",
    unknown: "登录过程中出现问题。请重试。",
    adminHintLabel: "致管理员：",
    hintTokenNotVerified:
      "ID 令牌的签发者、受众或签名与已保存的 SSO 设置不匹配。请在 SSO 设置中检查签发者 URL 和客户端 ID，然后重新尝试登录。",
    hintMissingUserInfo:
      "身份提供商必须在 ID 令牌或用户信息中包含电子邮件声明和主题（sub）。请在身份提供商中添加电子邮件声明（对于 AD FS：添加一条传出声明规则），然后重新尝试登录。",
    hintProviderMisconfigured:
      "请检查 SSO 设置中的客户端 ID 和密钥是否为最新（密钥过期是常见原因），然后重新保存签发者 URL 以刷新提供商的端点，并运行测试登录。",
    hintIdpRejectedRequest:
      "请检查 SSO 设置中显示的重定向 URI 是否已在身份提供商处注册，以及客户端 ID 和密钥是否为最新。",
    hintAccountNotLinked: "请检查此组织中是否存在使用身份提供商发送的电子邮件地址的用户。",
    referenceLabel: "参考编号：",
    providerMessageLabel: "来自您的登录提供商的消息：",
    referenceHelp: "请将此参考编号提供给您的管理员；它用于在日志中标识此次登录尝试。",
    copy: "复制",
    copied: "已复制",
  },
  zh_TW: {
    title: "SSO 登入失敗",
    linkEmailTitle: "請查看您的電子郵件",
    linkEmailSent: "要將單一登入連結到您現有的帳戶，請依照我們剛才寄給您的電子郵件中的說明操作。",
    cancelled: "登入已被您的身分提供者取消或拒絕。",
    expired: "登入嘗試已逾期，或在其他瀏覽器中開始。請再試一次。",
    idpRejectedRequest: "您的身分提供者拒絕了登入請求。",
    idpUnavailable: "您的身分提供者回報了暫時性問題。請在幾分鐘後再試一次。",
    idpTimeout: "您的身分提供者未及時回應。請再試一次。",
    tokenNotVerified: "無法驗證來自您身分提供者的登入回應。",
    missingUserInfo: "您的身分提供者未傳送您帳戶的電子郵件地址或使用者 ID。",
    emailRequired: "您的身分提供者未傳送電子郵件地址。請聯絡您的管理員。",
    providerMisconfigured: "此組織的 SSO 設定不正確。請聯絡您的管理員。",
    accountNotLinked: "您的 SSO 帳戶無法連結到此組織中的使用者。請聯絡您的管理員。",
    signupDisabled: "此組織中不存在對應您 SSO 帳戶的使用者。",
    accountUnavailable: "您的帳戶在此組織中已無法使用。請聯絡您的管理員。",
    lastPasswordAdmin:
      "您的帳戶是最後一位可以不透過單一登入即可登入的管理員，因此無法連結到 SSO。請使用密碼登入。",
    identityChanged: "您的單一登入身分與連結到您帳戶的身分不符。請聯絡您的管理員。",
    unknown: "登入時發生問題。請再試一次。",
    adminHintLabel: "給管理員：",
    hintTokenNotVerified:
      "ID 權杖的發行者、對象或簽章與已儲存的 SSO 設定不符。請在 SSO 設定中檢查發行者 URL 與用戶端 ID，然後重新嘗試登入。",
    hintMissingUserInfo:
      "身分提供者必須在 ID 權杖或使用者資訊中包含電子郵件宣告與主體（sub）。請在身分提供者中新增電子郵件宣告（AD FS 請新增一條傳出宣告規則），然後重新嘗試登入。",
    hintProviderMisconfigured:
      "請檢查 SSO 設定中的用戶端 ID 與密鑰是否為最新（密鑰過期是常見原因），然後重新儲存發行者 URL 以重新整理提供者的端點，並執行測試登入。",
    hintIdpRejectedRequest:
      "請檢查 SSO 設定中顯示的重新導向 URI 是否已在身分提供者處註冊，以及用戶端 ID 與密鑰是否為最新。",
    hintAccountNotLinked: "請檢查此組織中是否存在使用身分提供者所傳送電子郵件地址的使用者。",
    referenceLabel: "參考編號：",
    providerMessageLabel: "來自您的登入提供者的訊息：",
    referenceHelp: "請將此參考編號提供給您的管理員；它用於在記錄中識別此次登入嘗試。",
    copy: "複製",
    copied: "已複製",
  },
  nl_NL: {
    title: "SSO-aanmelding mislukt",
    linkEmailTitle: "Controleer je e-mail",
    linkEmailSent:
      "Volg de instructies in de e-mail die we je zojuist hebben gestuurd om single sign-on te koppelen aan je bestaande account.",
    cancelled: "Aanmelden is geannuleerd of geweigerd door je identiteitsprovider.",
    expired:
      "De aanmeldpoging is verlopen of is gestart in een andere browser. Probeer het opnieuw.",
    idpRejectedRequest: "Je identiteitsprovider heeft het aanmeldverzoek geweigerd.",
    idpUnavailable:
      "Je identiteitsprovider meldde een tijdelijk probleem. Probeer het over een paar minuten opnieuw.",
    idpTimeout: "Je identiteitsprovider heeft niet op tijd geantwoord. Probeer het opnieuw.",
    tokenNotVerified:
      "Het aanmeldantwoord van je identiteitsprovider kon niet worden geverifieerd.",
    missingUserInfo:
      "Je identiteitsprovider heeft geen e-mailadres of gebruikers-ID voor je account verzonden.",
    emailRequired:
      "Je identiteitsprovider heeft geen e-mailadres verzonden. Neem contact op met je beheerder.",
    providerMisconfigured:
      "Single sign-on is niet correct ingesteld voor deze organisatie. Neem contact op met je beheerder.",
    accountNotLinked:
      "Je SSO-account kon niet worden gekoppeld aan een gebruiker in deze organisatie. Neem contact op met je beheerder.",
    signupDisabled: "Er bestaat geen gebruiker voor je SSO-account in deze organisatie.",
    accountUnavailable:
      "Je account is niet langer beschikbaar in deze organisatie. Neem contact op met je beheerder.",
    lastPasswordAdmin:
      "Je account is de laatste beheerder die kan aanmelden zonder single sign-on, dus het kan niet worden gekoppeld aan SSO. Meld je aan met je wachtwoord.",
    identityChanged:
      "Je single sign-on-identiteit komt niet overeen met die gekoppeld aan je account. Neem contact op met je beheerder.",
    unknown: "Er is iets misgegaan tijdens het aanmelden. Probeer het opnieuw.",
    adminHintLabel: "Voor beheerders:",
    hintTokenNotVerified:
      "De uitgever, doelgroep of handtekening van het ID-token komt niet overeen met de opgeslagen SSO-instellingen. Controleer de Issuer-URL en Client-ID in de SSO-instellingen en probeer dan opnieuw aan te melden.",
    hintMissingUserInfo:
      "De identiteitsprovider moet een e-mailclaim en een subject (sub) bevatten in het ID-token of de gebruikersinfo. Voeg de e-mailclaim toe bij de identiteitsprovider (voor AD FS: een uitgaande claimregel) en probeer dan opnieuw aan te melden.",
    hintProviderMisconfigured:
      "Controleer of de Client-ID en het secret in de SSO-instellingen actueel zijn (een verlopen secret is een veelvoorkomende oorzaak), sla daarna de Issuer-URL opnieuw op om de eindpunten van de provider te vernieuwen en voer de test-aanmelding uit.",
    hintIdpRejectedRequest:
      "Controleer of de redirect-URI die in de SSO-instellingen wordt getoond, is geregistreerd bij de identiteitsprovider en of de Client-ID en het secret actueel zijn.",
    hintAccountNotLinked:
      "Controleer of de gebruiker in deze organisatie bestaat met het e-mailadres dat de identiteitsprovider verzendt.",
    referenceLabel: "Referentie:",
    providerMessageLabel: "Bericht van je aanmeldprovider:",
    referenceHelp:
      "Geef deze referentie door aan je beheerder; deze identificeert deze aanmeldpoging in de logs.",
    copy: "Kopiëren",
    copied: "Gekopieerd",
  },
  nb_NO: {
    title: "SSO-innlogging mislyktes",
    linkEmailTitle: "Sjekk e-posten din",
    linkEmailSent:
      "For å koble single sign-on til den eksisterende kontoen din, følg instruksjonene i e-posten vi nettopp sendte deg.",
    cancelled: "Innloggingen ble avbrutt eller avvist av identitetsleverandøren din.",
    expired: "Innloggingsforsøket utløp, eller ble startet i en annen nettleser. Prøv igjen.",
    idpRejectedRequest: "Identitetsleverandøren din avviste innloggingsforespørselen.",
    idpUnavailable:
      "Identitetsleverandøren din rapporterte et midlertidig problem. Prøv igjen om noen minutter.",
    idpTimeout: "Identitetsleverandøren din svarte ikke i tide. Prøv igjen.",
    tokenNotVerified: "Innloggingssvaret fra identitetsleverandøren din kunne ikke bekreftes.",
    missingUserInfo:
      "Identitetsleverandøren din sendte ikke en e-postadresse eller bruker-ID for kontoen din.",
    emailRequired:
      "Identitetsleverandøren din sendte ingen e-postadresse. Kontakt administratoren din.",
    providerMisconfigured:
      "SSO er ikke riktig satt opp for denne organisasjonen. Kontakt administratoren din.",
    accountNotLinked:
      "SSO-kontoen din kunne ikke knyttes til en bruker i denne organisasjonen. Kontakt administratoren din.",
    signupDisabled: "Det finnes ingen bruker for SSO-kontoen din i denne organisasjonen.",
    accountUnavailable:
      "Kontoen din er ikke lenger tilgjengelig i denne organisasjonen. Kontakt administratoren din.",
    lastPasswordAdmin:
      "Kontoen din er den siste administratoren som kan logge inn uten single sign-on, så den kan ikke kobles til SSO. Logg inn med passordet ditt.",
    identityChanged:
      "Single sign-on-identiteten din stemmer ikke med den som er koblet til kontoen din. Kontakt administratoren din.",
    unknown: "Noe gikk galt under innloggingen. Prøv igjen.",
    adminHintLabel: "For administratorer:",
    hintTokenNotVerified:
      "Utsteder, mottaker eller signatur i ID-tokenet samsvarer ikke med de lagrede SSO-innstillingene. Sjekk utsteder-URL og klient-ID i SSO-innstillinger, og prøv å logge inn på nytt.",
    hintMissingUserInfo:
      "Identitetsleverandøren må inkludere et e-post-claim og et subject (sub) i ID-tokenet eller userinfo. Legg til e-post-claimet hos identitetsleverandøren (for AD FS: en utgående claim-regel), og prøv å logge inn på nytt.",
    hintProviderMisconfigured:
      "Sjekk at klient-ID og -hemmelighet i SSO-innstillinger er gyldige (en utløpt hemmelighet er en vanlig årsak), lagre deretter utsteder-URLen på nytt for å oppdatere leverandørens endepunkter, og kjør test-innlogging.",
    hintIdpRejectedRequest:
      "Sjekk at redirect-URIen vist i SSO-innstillinger er registrert hos identitetsleverandøren, og at klient-ID og -hemmelighet er gyldige.",
    hintAccountNotLinked:
      "Sjekk at brukeren finnes i denne organisasjonen med e-postadressen identitetsleverandøren sender.",
    referenceLabel: "Referanse:",
    providerMessageLabel: "Melding fra innloggingsleverandøren din:",
    referenceHelp:
      "Gi denne referansen til administratoren din; den identifiserer dette innloggingsforsøket i loggene.",
    copy: "Kopier",
    copied: "Kopiert",
  },
  sv_SE: {
    title: "SSO-inloggning misslyckades",
    linkEmailTitle: "Kontrollera din e-post",
    linkEmailSent:
      "För att koppla enkel inloggning till ditt befintliga konto, följ instruktionerna i e-postmeddelandet vi just skickade till dig.",
    cancelled: "Inloggningen avbröts eller nekades av din identitetsleverantör.",
    expired:
      "Inloggningsförsöket upphörde att gälla eller startades i en annan webbläsare. Försök igen.",
    idpRejectedRequest: "Din identitetsleverantör avvisade inloggningsbegäran.",
    idpUnavailable:
      "Din identitetsleverantör rapporterade ett tillfälligt problem. Försök igen om några minuter.",
    idpTimeout: "Din identitetsleverantör svarade inte i tid. Försök igen.",
    tokenNotVerified: "Inloggningssvaret från din identitetsleverantör kunde inte verifieras.",
    missingUserInfo:
      "Din identitetsleverantör skickade inte en e-postadress eller ett användar-ID för ditt konto.",
    emailRequired:
      "Din identitetsleverantör skickade ingen e-postadress. Kontakta din administratör.",
    providerMisconfigured:
      "SSO är inte korrekt konfigurerat för den här organisationen. Kontakta din administratör.",
    accountNotLinked:
      "Ditt SSO-konto kunde inte kopplas till en användare i den här organisationen. Kontakta din administratör.",
    signupDisabled: "Det finns ingen användare för ditt SSO-konto i den här organisationen.",
    accountUnavailable:
      "Ditt konto är inte längre tillgängligt i den här organisationen. Kontakta din administratör.",
    lastPasswordAdmin:
      "Ditt konto är den sista administratören som kan logga in utan enkel inloggning, så det kan inte kopplas till SSO. Logga in med ditt lösenord.",
    identityChanged:
      "Din identitet för enkel inloggning matchar inte den som är kopplad till ditt konto. Kontakta din administratör.",
    unknown: "Något gick fel under inloggningen. Försök igen.",
    adminHintLabel: "För administratörer:",
    hintTokenNotVerified:
      "ID-tokens utfärdare, målgrupp eller signatur matchar inte de sparade SSO-inställningarna. Kontrollera utfärdar-URL och klient-ID i SSO-inställningarna och försök logga in igen.",
    hintMissingUserInfo:
      "Identitetsleverantören måste inkludera ett e-postanspråk och ett ämne (sub) i ID-token eller användarinformationen. Lägg till e-postanspråket hos identitetsleverantören (för AD FS: en utgående anspråksregel) och försök logga in igen.",
    hintProviderMisconfigured:
      "Kontrollera att klient-ID och klienthemlighet i SSO-inställningarna är aktuella (en utgången hemlighet är en vanlig orsak), spara sedan utfärdar-URL:en igen för att uppdatera leverantörens slutpunkter och kör testinloggningen.",
    hintIdpRejectedRequest:
      "Kontrollera att redirect-URI:n som visas i SSO-inställningarna är registrerad hos identitetsleverantören och att klient-ID och klienthemlighet är aktuella.",
    hintAccountNotLinked:
      "Kontrollera att användaren finns i den här organisationen med den e-postadress som identitetsleverantören skickar.",
    referenceLabel: "Referens:",
    providerMessageLabel: "Meddelande från din inloggningsleverantör:",
    referenceHelp:
      "Ge den här referensen till din administratör; den identifierar det här inloggningsförsöket i loggarna.",
    copy: "Kopiera",
    copied: "Kopierat",
  },
  fi_FI: {
    title: "SSO-kirjautuminen epäonnistui",
    linkEmailTitle: "Tarkista sähköpostisi",
    linkEmailSent:
      "Yhdistääksesi kertakirjautumisen olemassa olevaan tiliisi, noudata juuri lähettämämme sähköpostin ohjeita.",
    cancelled: "Kirjautumisen peruutti tai hylkäsi identiteetintarjoajasi.",
    expired: "Kirjautumisyritys vanhentui tai aloitettiin toisessa selaimessa. Yritä uudelleen.",
    idpRejectedRequest: "Identiteetintarjoajasi hylkäsi kirjautumispyynnön.",
    idpUnavailable:
      "Identiteetintarjoajasi ilmoitti tilapäisestä ongelmasta. Yritä uudelleen muutaman minuutin kuluttua.",
    idpTimeout: "Identiteetintarjoajasi ei vastannut ajoissa. Yritä uudelleen.",
    tokenNotVerified: "Identiteetintarjoajasi kirjautumisvastausta ei voitu vahvistaa.",
    missingUserInfo:
      "Identiteetintarjoajasi ei lähettänyt sähköpostiosoitetta tai käyttäjätunnusta tilillesi.",
    emailRequired:
      "Identiteetintarjoajasi ei lähettänyt sähköpostiosoitetta. Ota yhteyttä ylläpitäjään.",
    providerMisconfigured:
      "SSO:ta ei ole määritetty oikein tälle organisaatiolle. Ota yhteyttä ylläpitäjään.",
    accountNotLinked:
      "SSO-tiliäsi ei voitu yhdistää tämän organisaation käyttäjään. Ota yhteyttä ylläpitäjään.",
    signupDisabled: "Tässä organisaatiossa ei ole käyttäjää SSO-tilillesi.",
    accountUnavailable:
      "Tilisi ei ole enää käytettävissä tässä organisaatiossa. Ota yhteyttä ylläpitäjään.",
    lastPasswordAdmin:
      "Tilisi on viimeinen ylläpitäjä, joka voi kirjautua ilman kertakirjautumista, joten sitä ei voi yhdistää SSO:hon. Kirjaudu salasanallasi.",
    identityChanged:
      "Kertakirjautumisidentiteettisi ei vastaa tiliisi yhdistettyä identiteettiä. Ota yhteyttä ylläpitäjään.",
    unknown: "Jokin meni pieleen kirjautumisen aikana. Yritä uudelleen.",
    adminHintLabel: "Ylläpitäjille:",
    hintTokenNotVerified:
      "ID-tokenin myöntäjä, yleisö tai allekirjoitus ei vastaa tallennettuja SSO-asetuksia. Tarkista myöntäjän URL ja asiakastunnus SSO-asetuksista ja yritä kirjautua sisään uudelleen.",
    hintMissingUserInfo:
      "Identiteetintarjoajan on sisällytettävä sähköpostiväite ja kohde (sub) ID-tokeniin tai käyttäjätietoihin. Lisää sähköpostiväite identiteetintarjoajalle (AD FS:ssä: lähtevä väitesääntö) ja yritä kirjautua sisään uudelleen.",
    hintProviderMisconfigured:
      "Tarkista, että SSO-asetusten asiakastunnus ja -salaisuus ovat ajan tasalla (vanhentunut salaisuus on yleinen syy), tallenna sitten myöntäjän URL uudelleen päivittääksesi tarjoajan päätepisteet ja suorita testikirjautuminen.",
    hintIdpRejectedRequest:
      "Tarkista, että SSO-asetuksissa näkyvä uudelleenohjaus-URI on rekisteröity identiteetintarjoajalle ja että asiakastunnus ja -salaisuus ovat ajan tasalla.",
    hintAccountNotLinked:
      "Tarkista, että käyttäjä on olemassa tässä organisaatiossa identiteetintarjoajan lähettämällä sähköpostiosoitteella.",
    referenceLabel: "Viite:",
    providerMessageLabel: "Viesti kirjautumispalvelusi tarjoajalta:",
    referenceHelp:
      "Anna tämä viite ylläpitäjällesi; se yksilöi tämän kirjautumisyrityksen lokeissa.",
    copy: "Kopioi",
    copied: "Kopioitu",
  },
  da_DK: {
    title: "SSO-login mislykkedes",
    linkEmailTitle: "Tjek din e-mail",
    linkEmailSent:
      "For at forbinde single sign-on til din eksisterende konto skal du følge instruktionerne i den e-mail, vi lige har sendt dig.",
    cancelled: "Login blev annulleret eller afvist af din identitetsudbyder.",
    expired: "Loginforsøget udløb eller blev startet i en anden browser. Prøv igen.",
    idpRejectedRequest: "Din identitetsudbyder afviste loginanmodningen.",
    idpUnavailable:
      "Din identitetsudbyder rapporterede et midlertidigt problem. Prøv igen om et par minutter.",
    idpTimeout: "Din identitetsudbyder svarede ikke i tide. Prøv igen.",
    tokenNotVerified: "Loginsvaret fra din identitetsudbyder kunne ikke verificeres.",
    missingUserInfo:
      "Din identitetsudbyder sendte ikke en e-mailadresse eller bruger-id for din konto.",
    emailRequired: "Din identitetsudbyder sendte ingen e-mailadresse. Kontakt din administrator.",
    providerMisconfigured:
      "Single sign-on er ikke konfigureret korrekt for denne organisation. Kontakt din administrator.",
    accountNotLinked:
      "Din SSO-konto kunne ikke knyttes til en bruger i denne organisation. Kontakt din administrator.",
    signupDisabled: "Der findes ingen bruger til din SSO-konto i denne organisation.",
    accountUnavailable:
      "Din konto er ikke længere tilgængelig i denne organisation. Kontakt din administrator.",
    lastPasswordAdmin:
      "Din konto er den sidste administrator, der kan logge ind uden single sign-on, så den kan ikke tilsluttes SSO. Log ind med din adgangskode.",
    identityChanged:
      "Din single sign-on-identitet stemmer ikke overens med den, der er tilknyttet din konto. Kontakt din administrator.",
    unknown: "Der opstod en fejl under login. Prøv igen.",
    adminHintLabel: "Til administratorer:",
    hintTokenNotVerified:
      "ID-tokenets udsteder, målgruppe eller signatur stemmer ikke overens med de gemte SSO-indstillinger. Tjek udsteder-URL'en og klient-id'et i SSO-indstillingerne, og forsøg at logge ind igen.",
    hintMissingUserInfo:
      "Identitetsudbyderen skal inkludere et e-mail-claim og et emne (sub) i ID-tokenet eller brugeroplysningerne. Tilføj e-mail-claimet hos identitetsudbyderen (for AD FS: en udgående claim-regel), og forsøg at logge ind igen.",
    hintProviderMisconfigured:
      "Tjek, at klient-id og hemmelighed i SSO-indstillingerne er opdaterede (en udløbet hemmelighed er en almindelig årsag), gem derefter udsteder-URL'en igen for at opdatere udbyderens endepunkter, og kør testlogin.",
    hintIdpRejectedRequest:
      "Tjek, at redirect-URI'en, der vises i SSO-indstillingerne, er registreret hos identitetsudbyderen, og at klient-id og hemmelighed er opdaterede.",
    hintAccountNotLinked:
      "Tjek, at brugeren findes i denne organisation med den e-mailadresse, som identitetsudbyderen sender.",
    referenceLabel: "Reference:",
    providerMessageLabel: "Besked fra din login-udbyder:",
    referenceHelp:
      "Giv denne reference til din administrator; den identificerer dette loginforsøg i logfilerne.",
    copy: "Kopiér",
    copied: "Kopieret",
  },
  is_IS: {
    title: "Innskráning með SSO mistókst",
    linkEmailTitle: "Athugaðu tölvupóstinn þinn",
    linkEmailSent:
      "Til að tengja einfalda innskráningu við núverandi reikning þinn skaltu fylgja leiðbeiningunum í tölvupóstinum sem við sendum þér rétt í þessu.",
    cancelled: "Innskráningu var hætt við eða hafnað af auðkennisveitunni þinni.",
    expired: "Innskráningartilraunin rann út eða var hafin í öðrum vafra. Reyndu aftur.",
    idpRejectedRequest: "Auðkennisveitan þín hafnaði innskráningarbeiðninni.",
    idpUnavailable:
      "Auðkennisveitan þín tilkynnti um tímabundið vandamál. Reyndu aftur eftir nokkrar mínútur.",
    idpTimeout: "Auðkennisveitan þín svaraði ekki í tæka tíð. Reyndu aftur.",
    tokenNotVerified: "Ekki var hægt að staðfesta innskráningarsvarið frá auðkennisveitunni þinni.",
    missingUserInfo:
      "Auðkennisveitan þín sendi ekki netfang eða notandaauðkenni fyrir reikninginn þinn.",
    emailRequired: "Auðkennisveitan þín sendi ekkert netfang. Hafðu samband við kerfisstjóra.",
    providerMisconfigured:
      "SSO er ekki rétt uppsett fyrir þessa stofnun. Hafðu samband við kerfisstjóra.",
    accountNotLinked:
      "Ekki var hægt að tengja SSO-reikninginn þinn við notanda í þessari stofnun. Hafðu samband við kerfisstjóra.",
    signupDisabled: "Enginn notandi er til fyrir SSO-reikninginn þinn í þessari stofnun.",
    accountUnavailable:
      "Reikningurinn þinn er ekki lengur tiltækur í þessari stofnun. Hafðu samband við kerfisstjóra.",
    lastPasswordAdmin:
      "Reikningurinn þinn er síðasti kerfisstjórinn sem getur skráð sig inn án einfaldrar innskráningar, svo ekki er hægt að tengja hann við SSO. Skráðu þig inn með lykilorðinu þínu.",
    identityChanged:
      "Auðkenni einfaldrar innskráningar þinnar samsvarar ekki því sem er tengt reikningnum þínum. Hafðu samband við kerfisstjóra.",
    unknown: "Eitthvað fór úrskeiðis við innskráningu. Reyndu aftur.",
    adminHintLabel: "Fyrir kerfisstjóra:",
    hintTokenNotVerified:
      "Útgefandi, markhópur eða undirskrift auðkennismerkisins samsvarar ekki vistuðum SSO-stillingum. Athugaðu útgefanda-URL og biðlaraauðkenni í SSO-stillingum og reyndu síðan að skrá þig inn aftur.",
    hintMissingUserInfo:
      "Auðkennisveitan verður að innihalda netfangskröfu og viðfang (sub) í auðkennismerkinu eða notandaupplýsingum. Bættu netfangskröfunni við hjá auðkennisveitunni (fyrir AD FS: útgangskröfureglu) og reyndu síðan að skrá þig inn aftur.",
    hintProviderMisconfigured:
      "Athugaðu að biðlaraauðkenni og leyndarmál í SSO-stillingum séu í gildi (útrunnið leyndarmál er algeng orsök), vistaðu síðan útgefanda-URL aftur til að endurnýja endapunkta veitunnar og keyrðu prófunarinnskráningu.",
    hintIdpRejectedRequest:
      "Athugaðu að endurbeiningar-URI sem birt er í SSO-stillingum sé skráð hjá auðkennisveitunni og að biðlaraauðkenni og leyndarmál séu í gildi.",
    hintAccountNotLinked:
      "Athugaðu að notandinn sé til í þessari stofnun með því netfangi sem auðkennisveitan sendir.",
    referenceLabel: "Tilvísun:",
    providerMessageLabel: "Skilaboð frá innskráningarveitunni þinni:",
    referenceHelp:
      "Gefðu kerfisstjóranum þínum þessa tilvísun; hún auðkennir þessa innskráningartilraun í annálunum.",
    copy: "Afrita",
    copied: "Afritað",
  },
};
